import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { 
  DollarSign, CheckCircle, ArrowRight, Wallet, 
  TrendingUp, Shield, Zap, Users, Menu, X
} from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";

const CreatorsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['landing', 'common']);
  const { isAuthenticated, user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#05060D] overflow-hidden relative">
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-cyan-600/10 rounded-full blur-3xl"></div>
      </div>
      
      {/* Navigation - Safari-safe */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass" style={{ WebkitBackdropFilter: 'blur(12px)' }}>
        {/* Desktop Header */}
        <div className="hidden md:flex max-w-7xl mx-auto h-16 px-6 items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_friend-doc-view/artifacts/09b4legs_Conscious%20Creator%20logoet.png"
              alt="EleKtro"
              className="w-10 h-10"
            />
            <div className="flex flex-col">
              <span className="text-lg font-bold text-white leading-tight">EleKtro Universal</span>
              <span className="text-[10px] text-gray-500 leading-tight">Conscious Creator</span>
            </div>
          </Link>
          
          <div className="flex items-center gap-4">
            {/* Language Switcher */}
            <LanguageSwitcher />
            
            {isAuthenticated && user?.user_type === "creator" ? (
              <button
                onClick={() => navigate("/creator")}
                data-testid="nav-dashboard-btn"
                className="btn-primary px-6 py-2.5 rounded-lg font-semibold text-white"
              >
                {t('landing:nav.creatorDashboard')}
              </button>
            ) : (
              <>
                <Link to="/" className="text-gray-400 hover:text-white transition-colors text-sm">
                  {t('landing:nav.switchToBusiness')}
                </Link>
                <button
                  onClick={() => navigate("/auth?type=creator")}
                  data-testid="nav-creator-login"
                  className="text-gray-400 hover:text-white transition-colors text-sm"
                >
                  {t('landing:nav.login')}
                </button>
                <button
                  onClick={() => navigate("/auth?type=creator")}
                  data-testid="nav-creator-signup"
                  className="px-5 py-2 rounded-lg bg-slate-800 text-white font-semibold border border-slate-600 hover:bg-slate-700 hover:border-slate-500 transition-all duration-200"
                >
                  {t('landing:nav.startEarning')}
                </button>
              </>
            )}
          </div>
        </div>
        
        {/* Mobile Header - Full brand + hamburger only */}
        <div className="md:hidden">
          <div 
            className="flex items-center justify-between px-4"
            style={{ height: '64px', minHeight: '64px', maxHeight: '64px' }}
          >
            {/* Logo + Full Brand */}
            <Link 
              to="/" 
              className="flex items-center gap-2"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_friend-doc-view/artifacts/09b4legs_Conscious%20Creator%20logoet.png"
                alt="EleKtro Logo"
                style={{ width: '28px', height: '28px', flexShrink: 0 }}
              />
              <div>
                <div 
                  className="font-bold font-['Unbounded'] gradient-text"
                  style={{ fontSize: '13px', lineHeight: '1.2' }}
                >
                  EleKtro Universal
                </div>
                <div 
                  className="text-gray-500"
                  style={{ fontSize: '10px', lineHeight: '1.2' }}
                >
                  Conscious Creator
                </div>
              </div>
            </Link>
            
            {/* Hamburger button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-white"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
          
          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div 
              className="absolute top-16 left-0 right-0 bg-[#05060D] border-b border-white/10 p-4 space-y-3"
              style={{ WebkitBackdropFilter: 'blur(12px)', backdropFilter: 'blur(12px)' }}
            >
              <Link 
                to="/"
                className="block py-2 text-gray-400 hover:text-white transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('landing:nav.switchToBusiness')}
              </Link>
              {isAuthenticated && user?.user_type === "creator" ? (
                <button
                  onClick={() => { navigate("/creator"); setMobileMenuOpen(false); }}
                  className="w-full btn-primary py-3 rounded-xl font-semibold text-white"
                >
                  {t('landing:nav.creatorDashboard')}
                </button>
              ) : (
                <>
                  <button
                    onClick={() => { navigate("/auth?type=creator"); setMobileMenuOpen(false); }}
                    className="block py-2 text-gray-400 hover:text-white transition-colors w-full text-left"
                  >
                    {t('landing:nav.login')}
                  </button>
                  <button
                    onClick={() => { navigate("/auth?type=creator"); setMobileMenuOpen(false); }}
                    className="w-full btn-primary py-3 rounded-xl font-semibold text-white"
                  >
                    {t('landing:nav.startEarning')}
                  </button>
                </>
              )}
              <div className="pt-2 border-t border-white/10">
                <LanguageSwitcher />
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative z-10">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold font-['Unbounded'] leading-tight mb-6">
            <span className="text-white">{t('landing:creators.hero.title')}</span>{" "}
            <span className="gradient-text">{t('landing:creators.hero.titleHighlight')}</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
            {t('landing:creators.hero.subtitle')}
          </p>
          <button
            onClick={() => navigate("/auth?type=creator")}
            data-testid="hero-creator-cta"
            className="px-8 py-4 rounded-xl bg-slate-800 text-white font-bold text-lg border border-slate-600 hover:bg-slate-700 hover:border-slate-500 transition-all duration-200 inline-flex items-center gap-2"
          >
            {t('landing:creators.hero.cta')}
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* How Creators Earn */}
      <section className="py-20 px-6 relative z-10">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold font-['Unbounded'] text-center mb-12 text-white">
            {t('landing:creators.howItWorks.title')}
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                icon: Zap, 
                title: t('landing:creators.howItWorks.step1.title'),
                desc: t('landing:creators.howItWorks.step1.description'),
                step: "01" 
              },
              { 
                icon: TrendingUp, 
                title: t('landing:creators.howItWorks.step2.title'),
                desc: t('landing:creators.howItWorks.step2.description'),
                step: "02" 
              },
              { 
                icon: DollarSign, 
                title: t('landing:creators.howItWorks.step3.title'),
                desc: t('landing:creators.howItWorks.step3.description'),
                step: "03" 
              }
            ].map((item, i) => (
              <div key={i} className="glass-card rounded-2xl p-6 relative overflow-hidden">
                <div className="absolute top-4 right-4 text-6xl font-bold text-white/5 font-['Unbounded']">
                  {item.step}
                </div>
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-purple-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-20 px-6 relative z-10">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold font-['Unbounded'] text-center mb-12 text-white">
            {t('landing:creators.benefits.title')}
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { 
                icon: Wallet, 
                title: t('landing:creators.benefits.performance.title'),
                desc: t('landing:creators.benefits.performance.description'),
                color: "cyan" 
              },
              { 
                icon: DollarSign, 
                title: t('landing:creators.benefits.instant.title'),
                desc: t('landing:creators.benefits.instant.description'),
                color: "green" 
              },
              { 
                icon: Shield, 
                title: t('landing:creators.benefits.network.title'),
                desc: t('landing:creators.benefits.network.description'),
                color: "purple" 
              },
              { 
                icon: Users, 
                title: t('landing:creators.benefits.tracking.title'),
                desc: t('landing:creators.benefits.tracking.description'),
                color: "blue" 
              }
            ].map((item, i) => (
              <div key={i} className="glass-card rounded-2xl p-6 flex gap-4 card-hover">
                <div className={`w-12 h-12 rounded-xl flex-shrink-0 flex items-center justify-center ${
                  item.color === "cyan" ? "bg-cyan-500/20" :
                  item.color === "green" ? "bg-green-500/20" :
                  item.color === "purple" ? "bg-purple-500/20" : "bg-blue-500/20"
                }`}>
                  <item.icon className={`w-6 h-6 ${
                    item.color === "cyan" ? "text-cyan-400" :
                    item.color === "green" ? "text-green-400" :
                    item.color === "purple" ? "text-purple-400" : "text-blue-400"
                  }`} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Earnings Breakdown */}
      <section className="py-20 px-6 relative z-10">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold font-['Unbounded'] text-center mb-8 text-white">
            {t('landing:creators.earnings.title')}
          </h2>
          
          <div className="glass-card rounded-2xl p-8 border border-purple-500/20">
            <div className="space-y-4">
              <div className="flex justify-between items-center p-4 rounded-xl bg-white/5">
                <span className="text-gray-400">{t('landing:creators.earnings.campaignBudget')}</span>
                <span className="text-white font-bold">€100</span>
              </div>
              <div className="flex justify-between items-center p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/30">
                <span className="text-cyan-400 font-medium">{t('landing:creators.earnings.yourEarnings')}</span>
                <span className="text-cyan-400 font-bold text-xl">€80</span>
              </div>
              <div className="flex justify-between items-center p-4 rounded-xl bg-white/5">
                <span className="text-gray-400">{t('landing:creators.earnings.platformFee')}</span>
                <span className="text-gray-500">€20</span>
              </div>
            </div>
            <p className="text-sm text-cyan-400 mt-4 text-center font-medium">
              {t('landing:creators.earnings.example')}
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          <div className="glass-card rounded-3xl p-12 border border-purple-500/20">
            <h2 className="text-3xl font-bold font-['Unbounded'] mb-4 text-white">
              {t('landing:creators.cta.title')}
            </h2>
            <p className="text-gray-400 mb-8">
              {t('landing:creators.cta.subtitle')}
            </p>
            <button
              onClick={() => navigate("/auth?type=creator")}
              data-testid="final-creator-cta"
              className="px-8 py-4 rounded-xl bg-slate-800 text-white font-bold text-lg border border-slate-600 hover:bg-slate-700 hover:border-slate-500 transition-all duration-200 inline-flex items-center gap-2"
            >
              {t('landing:creators.cta.button')}
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_friend-doc-view/artifacts/09b4legs_Conscious%20Creator%20logoet.png"
                alt="EleKtro"
                className="w-6 h-6"
              />
              <div className="flex flex-col">
                <span className="text-gray-400 text-sm leading-tight">EleKtro Universal</span>
                <span className="text-[10px] text-gray-600 leading-tight">Conscious Creator</span>
              </div>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <Link to="/" className="hover:text-white transition-colors">{t('landing:footer.forBusinesses')}</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">{t('landing:footer.privacy')}</Link>
              <Link to="/terms" className="hover:text-white transition-colors">{t('landing:footer.terms')}</Link>
              <Link to="/cookies" className="hover:text-white transition-colors">{t('landing:footer.cookies')}</Link>
              <LanguageSwitcher variant="minimal" />
            </div>
            <div className="text-sm text-gray-600">
              © 2026 EleKtro Universal
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CreatorsPage;
